

/* ==========================================================================
   SHARED CASE CARD VIEW MODEL & SECTION RENDERERS (/database/js/card-shared.js)
   Keeps mobile/desktop semantics aligned while allowing different geometry.
   ========================================================================== */

import {
  escapeHtml, generateInlineNoteToggle, formatCaseBrandTitle,
  slugifyUrlSegment, caseRouteSlug, sanitizeUrl
} from '../utils.js?v=20260819-audit2';
import { state, getCaseModeSelection } from '../state.js?v=20260819-audit2';
import { getCaseLayoutTag, getEffectiveSpecs } from '../compatibility-engine.js?v=20260819-audit2';

function rawDisplay(value, fallback = 'UNKNOWN') {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text === '' ? fallback : text;
}

function yesNoUnknown(value) {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (normalized === 'Y' || normalized === 'YES' || normalized === 'TRUE' || normalized === '1') return 'YES';
  if (normalized === 'N' || normalized === 'NO' || normalized === 'FALSE' || normalized === '0') return 'NO';
  return 'UNKNOWN';
}

function mountDisplay(value) {
  const text = rawDisplay(value);
  return text === 'UNKNOWN' ? text : `${text} Mounts`;
}

function priceDisplay(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '0') return 'N/A';
  return raw.startsWith('$') ? raw : `$${raw}`;
}

export function formatCpuClearance(c, activeSpecs) {
  const maxCpu = Number(activeSpecs?.maxCPU) || 0;
  if (maxCpu >= 9999) return c?.hasSpine ? 'OPEN · DYNAMIC' : 'OPEN';
  if (maxCpu > 0) return c?.hasSpine ? `${maxCpu} MM · DYNAMIC` : `${maxCpu} MM`;
  return c?._known?.maxCPU ? 'UNSUPPORTED' : 'UNKNOWN';
}

export function formatAioClearance(c, activeSpecs) {
  const maxAio = Number(activeSpecs?.maxAIO) || 0;
  if (maxAio >= 9999) return 'OPEN';
  if (maxAio > 0) return `${maxAio} MM`;
  return c?._known?.maxAIO ? 'NONE' : 'UNKNOWN';
}


function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function uniquePositiveSizes(values = []) {
  return [...new Set(values.map(Number).filter(v => Number.isFinite(v) && v > 0).map(v => Math.round(v)))].sort((a, b) => a - b);
}

function radiatorSupportEntries(c, activeSpecs) {
  const support = c?.radiatorSupport && typeof c.radiatorSupport === 'object' && !Array.isArray(c.radiatorSupport)
    ? c.radiatorSupport
    : null;
  const maxAio = Number(activeSpecs?.maxAIO);
  const knownAio = activeSpecs?.known?.maxAIO ?? c?._known?.maxAIO;
  if (knownAio && maxAio === 0) return [];
  const allowed = size => !Number.isFinite(maxAio) || maxAio <= 0 || maxAio >= 9999 || size <= maxAio;
  if (support) {
    return Object.entries(support)
      .map(([size, count]) => ({ size: Math.round(Number(size) || 0), count: Math.max(1, Math.round(Number(count) || 1)) }))
      .filter(entry => entry.size > 0 && allowed(entry.size))
      .sort((a, b) => a.size - b.size);
  }

  if (Array.isArray(c?.radiatorSupport)) {
    return uniquePositiveSizes(c.radiatorSupport).filter(allowed).map(size => ({ size, count: 1 }));
  }

  if (typeof c?.radiatorSupport === 'string') {
    const sizes = [...String(c.radiatorSupport).matchAll(/(?:^|\D)(120|140|240|280|360|420)(?=\D|$)/g)].map(match => Number(match[1]));
    if (sizes.length) return uniquePositiveSizes(sizes).filter(allowed).map(size => ({ size, count: 1 }));
  }
  return [];
}

export function formatRadiatorSupport(c, activeSpecs) {
  const entries = radiatorSupportEntries(c, activeSpecs);
  if (entries.length) return `${entries.map(({ size, count }) => count > 1 ? `${count}×${size}` : `${size}`).join(' / ')} MM`;
  return formatAioClearance(c, activeSpecs);
}

function normalizeMountZone(value = '') {
  const raw = String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (raw.includes('motherboard side') || raw.includes('mobo side')) return 'MOTHERBOARD SIDE';
  if (raw.includes('gpu side') || raw.includes('graphics card side')) return 'GPU SIDE';
  if (raw.includes('top') || raw.includes('upper')) return 'TOP';
  if (raw.includes('bottom') || raw.includes('lower')) return 'BOTTOM';
  if (raw.includes('front')) return 'FRONT';
  if (raw.includes('rear') || raw.includes('back')) return 'REAR';
  if (raw.includes('left')) return 'LEFT SIDE';
  if (raw.includes('right')) return 'RIGHT SIDE';
  if (raw.includes('side')) return 'SIDE';
  return raw ? raw.toUpperCase() : '';
}

function caseSpatialHints(c) {
  const raw = c?.spatialHints || c?.spatial_hints || c?.spatialEvidence || null;
  return raw && typeof raw === 'object' ? raw : {};
}

function hintSizes(hint = {}) {
  const counts = hint?.counts && typeof hint.counts === 'object' ? hint.counts : {};
  return uniquePositiveSizes([...(Array.isArray(hint?.sizes) ? hint.sizes : []), ...Object.keys(counts)]);
}

function hintCount(hint, size) {
  const counts = hint?.counts && typeof hint.counts === 'object' ? hint.counts : {};
  const count = Number(counts[String(Math.round(size))] ?? counts[size] ?? hint?.count);
  return Math.max(1, Math.round(Number.isFinite(count) ? count : 1));
}

function hintConditions(hint = {}) {
  const conditions = Array.isArray(hint?.conditions) ? hint.conditions : [];
  return conditions.map(v => String(v || '').trim().replace(/[_-]+/g, ' ').toUpperCase()).filter(Boolean);
}

function mountLocationLines(c, kind = 'fan', activeSpecs = null) {
  const hints = caseSpatialHints(c);
  const source = Array.isArray(hints?.[kind === 'radiator' ? 'radiators' : 'fans']) ? hints[kind === 'radiator' ? 'radiators' : 'fans'] : [];
  return source.map(hint => {
    const zone = normalizeMountZone(hint?.zone);
    const sizes = hintSizes(hint);
    if (!zone || !sizes.length) return '';
    const parts = sizes.map(size => {
      const count = hintCount(hint, size);
      return count > 1 ? `${count}×${size}` : `${size}`;
    });
    const qualifiers = [];
    if (hint?.snapEligible === false || String(hint?.placementConfidence || '').toLowerCase().includes('ambiguous')) qualifiers.push('LOCATION AMBIGUOUS');
    if (kind === 'radiator') {
      const stack = positiveNumber(hint?.maxStack);
      const rad = positiveNumber(hint?.maxRadiatorThickness);
      if (stack) qualifiers.push(`STACK ≤${Math.round(stack)}MM`);
      if (rad) qualifiers.push(`RAD ≤${Math.round(rad)}MM`);
      qualifiers.push(...hintConditions(hint));
    } else {
      const limits = hint?.maxFanThickness && typeof hint.maxFanThickness === 'object' ? hint.maxFanThickness : {};
      const knownLimits = uniquePositiveSizes(Object.values(limits));
      if (knownLimits.length === 1) qualifiers.push(`FAN ≤${knownLimits[0]}MM`);
    }
    return `${zone} · ${parts.join(' / ')}${qualifiers.length ? ` · ${qualifiers.join(' · ')}` : ''}`;
  }).filter(Boolean);
}

function fallbackFanMountLines(c) {
  const lines = [];
  for (const size of [60, 80, 92, 120, 140]) {
    const raw = c?.[`fans${size}`];
    if (raw === undefined || raw === null || String(raw).trim() === '') continue;
    const count = Number(raw);
    if (Number.isFinite(count) && count <= 0) continue;
    const text = Number.isFinite(count) ? `${Math.round(count)} MOUNT${Math.round(count) === 1 ? '' : 'S'}` : rawDisplay(raw);
    lines.push(`${size}MM · ${text}`);
  }
  return lines;
}

export function getCaseCardViewModel(c, idx, options = {}) {
  const cardType = options.cardType || 'feed';
  const surface = options.surface || 'desktop';
  const slug = caseRouteSlug(c);
  const hasModes = Array.isArray(c.modes) && c.modes.length > 0;
  const activeModeId = getCaseModeSelection(c, hasModes ? c.modes[0].mode_id : 'default');
  const activeSpecs = getEffectiveSpecs(c, activeModeId);
  const isAff = (c.url || '').includes('amazon.com/s?');
  const isPinned = state.sffPinnedCases.includes(idx) || cardType === 'compare';
  const idPrefix = surface === 'mobile' ? 'mob-' : '';
  const radiatorMounts = mountLocationLines(c, 'radiator', activeSpecs);
  const spatialFanMounts = mountLocationLines(c, 'fan', activeSpecs);
  const fanMounts = spatialFanMounts.length ? spatialFanMounts : fallbackFanMountLines(c);

  const staticDim = c.caseDimensions
    ? String(c.caseDimensions).replace(/x/gi, '×').toUpperCase()
    : 'UNKNOWN';

  return {
    c, idx, cardType, surface, slug,
    href: `/db/${slug}/`,
    broadNavigation: cardType === 'feed',
    formattedTitle: formatCaseBrandTitle(c.name),
    notes: c.specNotes || {},
    hasModes, activeModeId, activeSpecs,
    isSelected: state.selectedCaseIdx === idx,
    isPinned,
    layoutTag: getCaseLayoutTag(c),
    isAff,
    linkClass: isAff ? 'affiliate-link-type' : 'direct-link-type',
    linkText: cardType === 'single'
      ? (isAff ? 'AFFILIATE LINK' : 'OFFICIAL CASE PAGE')
      : (isAff ? 'AFFILIATE' : 'SELLER PAGE'),
    safeUrl: sanitizeUrl(c.url),
    buildsDrawerId: `builds-drawer-${idPrefix}${slug}-${idx}`,
    reviewsDrawerId: `reviews-drawer-${idPrefix}${slug}-${idx}`,
    display: {
      mobo: rawDisplay(Array.isArray(c._moboList) ? c._moboList.join(' / ') : ''),
      psu: rawDisplay(Array.isArray(c._psuList) ? c._psuList.join(' / ') : ''),
      pcie: c._known?.pcie
        ? `${rawDisplay(c.pcieSlots || `${c._pcie} Slots`)}${c.gpuRiser === 'Y' ? ' (Riser Inc.)' : ''}`
        : 'UNKNOWN',
      cpu: formatCpuClearance(c, activeSpecs),
      aio: formatRadiatorSupport(c, activeSpecs),
      radiatorMounts,
      gpu: c._isAPU
        ? 'APU ONLY'
        : `${activeSpecs.maxGPU}×${activeSpecs.maxGPUT}×${activeSpecs.maxGPUH} MM`,
      dimensions: staticDim,
      footprint: c.footprint ? `${c.footprint} CM²` : 'UNKNOWN',
      fans120: mountDisplay(c.fans120),
      fans140: mountDisplay(c.fans140),
      fanMounts,
      usbC: yesNoUnknown(c.usbC),
      drives25: rawDisplay(c.drives25),
      drives35: rawDisplay(c.drives35),
      price: priceDisplay(c.priceUSD),
      material: rawDisplay(c.material)
    }
  };
}

function finitePositive(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function updateCardClearanceGauges(el, c, result, formInputs) {
  if (!el || !c || !result) return;
  const idx = result.idx;
  const activeModeId = getCaseModeSelection(c, 'default');
  const activeSpecs = result.activeSpecs || getEffectiveSpecs(c, activeModeId);
  const userCpu = finitePositive(formInputs?.cpuH) || 47;
  const userGpuL = finitePositive(formInputs?.gpuL) || 304;
  const userGpuT = finitePositive(formInputs?.gpuT) || 40;
  const userGpuH = finitePositive(formInputs?.gpuH) || 137;
  const userRad = finitePositive(formInputs?.rad) || 240;

  const maxCpu = finitePositive(activeSpecs.maxCPU);
  const maxGpuL = finitePositive(activeSpecs.maxGPU);
  const maxGpuT = finitePositive(activeSpecs.maxGPUT);
  const maxGpuH = finitePositive(activeSpecs.maxGPUH);
  const maxAio = finitePositive(activeSpecs.maxAIO);

  const cpuFill = el.querySelector(`#gauge-cpu-fill-${idx}`) || el.querySelector('.gauge-cpu-fill');
  const cpuDeltaEl = el.querySelector(`#metric-cpu-delta-${idx}`) || el.querySelector('.metric-cpu-delta');
  if (activeSpecs.maxCPU >= 9999) {
    if (cpuFill) cpuFill.style.width = '100%';
    if (cpuDeltaEl) { cpuDeltaEl.innerText = 'UNRESTRICTED'; cpuDeltaEl.style.color = 'var(--sff-sky, #38bdf8)'; }
  } else if (maxCpu > 0) {
    const cpuPct = Math.min(100, Math.max(0, (userCpu / maxCpu) * 100));
    const cpuDelta = maxCpu - userCpu;
    if (cpuFill) {
      cpuFill.style.width = `${cpuPct}%`;
      cpuFill.style.background = cpuDelta < 0 ? '#f43f5e' : 'var(--sff-sky, #38bdf8)';
    }
    if (cpuDeltaEl) {
      cpuDeltaEl.innerText = cpuDelta >= 0 ? `+${cpuDelta}MM HEADROOM` : `${cpuDelta}MM CONFLICT`;
      cpuDeltaEl.style.color = cpuDelta >= 0 ? '#10b981' : '#f43f5e';
    }
  } else if (cpuDeltaEl) {
    cpuDeltaEl.innerText = 'CLEARANCE UNKNOWN';
    cpuDeltaEl.style.color = 'var(--sff-text-muted, #9ca3af)';
  }

  const gpuFill = el.querySelector(`#gauge-gpu-fill-${idx}`) || el.querySelector('.gauge-gpu-fill');
  const gpuDeltaEl = el.querySelector(`#metric-gpu-delta-${idx}`) || el.querySelector('.metric-gpu-delta');
  if (c._isAPU) {
    if (gpuFill) gpuFill.style.width = '0%';
    if (gpuDeltaEl) { gpuDeltaEl.innerText = 'APU EXCLUSIVE'; gpuDeltaEl.style.color = 'var(--sff-text-muted, #9ca3af)'; }
  } else if (activeSpecs.maxGPU >= 9999) {
    if (gpuFill) gpuFill.style.width = '100%';
    if (gpuDeltaEl) { gpuDeltaEl.innerText = 'UNRESTRICTED'; gpuDeltaEl.style.color = '#f59e0b'; }
  } else {
    const axes = [
      maxGpuL > 0 ? { key: 'LEN', ratio: userGpuL / maxGpuL, delta: maxGpuL - userGpuL } : null,
      maxGpuT > 0 ? { key: 'THICK', ratio: userGpuT / maxGpuT, delta: maxGpuT - userGpuT } : null,
      maxGpuH > 0 ? { key: 'HEIGHT', ratio: userGpuH / maxGpuH, delta: maxGpuH - userGpuH } : null
    ].filter(Boolean);

    if (axes.length) {
      const tightest = axes.reduce((a, b) => b.ratio > a.ratio ? b : a);
      const hasConflict = axes.some(axis => axis.delta < 0);
      const worstConflict = axes.filter(axis => axis.delta < 0).sort((a, b) => a.delta - b.delta)[0] || null;
      const gpuPct = Math.min(100, Math.max(0, tightest.ratio * 100));
      if (gpuFill) {
        gpuFill.style.width = `${gpuPct}%`;
        gpuFill.style.background = hasConflict ? '#f43f5e' : '#f59e0b';
      }
      if (gpuDeltaEl) {
        if (hasConflict && worstConflict) {
          gpuDeltaEl.innerText = `${worstConflict.delta}MM ${worstConflict.key} CONFLICT`;
          gpuDeltaEl.style.color = '#f43f5e';
        } else {
          gpuDeltaEl.innerText = `+${tightest.delta}MM ${tightest.key} HEADROOM`;
          gpuDeltaEl.style.color = '#10b981';
        }
      }
    } else if (gpuDeltaEl) {
      gpuDeltaEl.innerText = 'CLEARANCE UNKNOWN';
      gpuDeltaEl.style.color = 'var(--sff-text-muted, #9ca3af)';
    }
  }

  const aioFill = el.querySelector(`#gauge-aio-fill-${idx}`) || el.querySelector('.gauge-aio-fill');
  if (activeSpecs.maxAIO >= 9999) {
    if (aioFill) aioFill.style.width = '100%';
  } else if (maxAio > 0) {
    const aioPct = Math.min(100, Math.max(0, (userRad / maxAio) * 100));
    const aioConflict = userRad > maxAio;
    if (aioFill) {
      aioFill.style.width = `${aioPct}%`;
      aioFill.style.background = aioConflict ? '#f43f5e' : '#10b981';
    }
  } else if (aioFill) {
    aioFill.style.width = '0%';
  }
}

export function renderModeSelectorHtml(vm) {
  if (!vm.hasModes) return '';
  const optionsHtml = vm.c.modes.map(mode => `
    <option value="${escapeHtml(mode.mode_id)}" ${mode.mode_id === vm.activeModeId ? 'selected' : ''}>
      ${escapeHtml(mode.mode_name || mode.mode_id)}
    </option>`).join('');

  return `
    <div class="ws-mode-select-box case-mode-selector-wrapper font-mono">
      <label for="mode-select-${vm.idx}" class="ws-mode-lbl mode-select-label">LAYOUT CONFIG:</label>
      <select id="mode-select-${vm.idx}" class="sff-mode-select ws-mode-input font-mono" data-case-idx="${vm.idx}" data-case-name="${escapeHtml(vm.c.name)}">
        ${optionsHtml}
      </select>
    </div>`;
}

export function renderFormFactorSummaryHtml(vm) {
  const { notes, slug, idx } = vm;
  return `
    <div class="ws-form-factor-bar font-mono">
      <div class="ws-ff-pill">
        <span class="ws-ff-lbl">MOTHERBOARD</span>
        <strong class="ws-ff-val">${escapeHtml(vm.display.mobo)}</strong>
      </div>
      <div class="ws-ff-divider"></div>
      <div class="ws-ff-pill">
        <span class="ws-ff-lbl">POWER SUPPLY</span>
        <strong class="ws-ff-val">${escapeHtml(vm.display.psu)}</strong>
        <div class="notes-container">${generateInlineNoteToggle(notes, 'psu', slug, idx)}</div>
      </div>
      <div class="ws-ff-divider"></div>
      <div class="ws-ff-pill">
        <span class="ws-ff-lbl">PCIE SLOTS</span>
        <strong class="ws-ff-val">${escapeHtml(vm.display.pcie)}</strong>
        <div class="notes-container">${generateInlineNoteToggle(notes, 'pcie', slug, idx)}</div>
      </div>
    </div>`;
}

export function renderClearancePanelHtml(vm) {
  const { c, notes, slug, idx } = vm;
  return `
    <div class="ws-clearance-panel font-mono">
      <div class="ws-metric-box">
        <div class="ws-metric-head">
          <span class="ws-metric-lbl">CPU COOLER</span>
          <span class="ws-metric-val metric-cpu-val">${escapeHtml(vm.display.cpu)}</span>
        </div>
        <div class="gauge-track"><div class="gauge-fill gauge-cpu-fill" id="gauge-cpu-fill-${idx}"></div></div>
        <div class="ws-metric-foot">
          <span class="gauge-delta metric-cpu-delta" id="metric-cpu-delta-${idx}">CALCULATING...</span>
          <div class="notes-container">${generateInlineNoteToggle(notes, 'cpu', slug, idx)}</div>
        </div>
      </div>

      <div class="ws-metric-box">
        <div class="ws-metric-head">
          <span class="ws-metric-lbl">GRAPHICS CARD</span>
          <span class="ws-metric-val metric-gpu-val">${escapeHtml(vm.display.gpu)}</span>
        </div>
        <div class="gauge-track"><div class="gauge-fill gauge-gpu-fill" id="gauge-gpu-fill-${idx}"></div></div>
        <div class="ws-metric-foot">
          <span class="gauge-delta metric-gpu-delta" id="metric-gpu-delta-${idx}">CALCULATING...</span>
          <div class="notes-container">${generateInlineNoteToggle(notes, 'gpu-combined', slug, idx)}</div>
        </div>
      </div>

      <div class="ws-metric-box">
        <div class="ws-metric-head">
          <span class="ws-metric-lbl">AIO / RADIATOR</span>
          <span class="ws-metric-val metric-aio-val">${escapeHtml(vm.display.aio)}</span>
        </div>
        <div class="gauge-track"><div class="gauge-fill gauge-aio-fill" id="gauge-aio-fill-${idx}"></div></div>
        <div class="ws-metric-foot">
          <div class="ws-support-lines">
            ${vm.display.radiatorMounts.length
              ? vm.display.radiatorMounts.map(line => `<span class="ws-support-line ws-rad-location"><strong>${escapeHtml(line)}</strong></span>`).join('')
              : `<span class="ws-sub-note">${c.top_fan_clearance ? `MAX RAD: ${escapeHtml(c.top_fan_clearance)}MM` : 'LOCATION NOT SPECIFIED'}</span>`}
          </div>
          <div class="notes-container">${generateInlineNoteToggle(notes, 'aio', slug, idx)}</div>
        </div>
      </div>
    </div>`;
}

function renderStandardDataLine(line) {
  const parts = String(line || '').split('·').map(part => part.trim()).filter(Boolean);
  if (!parts.length) return '<span class="ws-cell-val">UNKNOWN</span>';
  const label = parts.shift();
  const value = parts.join(' · ');
  return value
    ? `<span class="ws-cell-val">${escapeHtml(label)}: <strong>${escapeHtml(value)}</strong></span>`
    : `<span class="ws-cell-val"><strong>${escapeHtml(label)}</strong></span>`;
}

export function renderSecondaryDataHtml(vm) {
  return `
    <div class="ws-data-grid font-mono">
      <div class="ws-data-cell">
        <span class="ws-cell-label">CASE DIMENSIONS</span>
        <span class="ws-cell-val">L×W×H: <strong>${escapeHtml(vm.display.dimensions)}</strong></span>
        <span class="ws-cell-val">FOOTPRINT: <strong>${escapeHtml(vm.display.footprint)}</strong></span>
      </div>
      <div class="ws-data-cell">
        <span class="ws-cell-label">FAN MOUNTS</span>
        <div class="ws-support-lines">
          ${vm.display.fanMounts.length
            ? vm.display.fanMounts.map(renderStandardDataLine).join('')
            : `<span class="ws-cell-val">LOCATION / SIZE: <strong>UNKNOWN</strong></span>`}
        </div>
      </div>
      <div class="ws-data-cell">
        <span class="ws-cell-label">I/O & DRIVES</span>
        <span class="ws-cell-val">FRONT USB-C: <strong>${escapeHtml(vm.display.usbC)}</strong></span>
        <span class="ws-cell-val">2.5&quot; SSD: <strong>${escapeHtml(vm.display.drives25)}</strong> | 3.5&quot; HDD: <strong>${escapeHtml(vm.display.drives35)}</strong></span>
      </div>
      <div class="ws-data-cell">
        <span class="ws-cell-label">PRICING & MATERIALS</span>
        <span class="ws-cell-val">MSRP: <strong>${escapeHtml(vm.display.price)}</strong></span>
        <span class="ws-cell-val">MATERIAL: <strong>${escapeHtml(vm.display.material)}</strong></span>
      </div>
    </div>`;
}

export function renderLayerBarHtml(vm) {
  if (vm.cardType === 'feed') return '';
  const escapedName = escapeHtml(vm.c.name);
  return `
    <div class="ws-layer-bar font-mono">
      <span class="ws-layer-label">MORE:</span>
      <div class="ws-layer-buttons">
        <button type="button" class="sff-community-builds-toggle-btn ws-layer-btn" data-slug="${vm.slug}" data-idx="${vm.idx}" data-target="${vm.buildsDrawerId}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          <span>BUILDS</span>
        </button>
        <button type="button" class="sff-gallery-layer-btn ws-layer-btn" data-idx="${vm.idx}" title="Open Giant Photo Gallery">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
          <span>GALLERY</span>
        </button>
        <button type="button" class="sff-reviews-layer-btn ws-layer-btn" data-slug="${vm.slug}" data-idx="${vm.idx}" data-target="${vm.reviewsDrawerId}" title="Case editorial reviews layer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
          <span>REVIEWS</span>
        </button>
        <button type="button" class="sff-3d-hook-btn ws-layer-btn" data-case-name="${escapedName}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
          <span>3D VIEW</span>
        </button>
      </div>
    </div>`;
}
